import React, { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";

export default function ResetPassword() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // Guard against rapid double-submit (see ForgotPassword.jsx for context).
  const inFlight = useRef(false);

  const tokenMissing = !token;

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    setError("");
    if (password.length < 6) {
      setError(t("reset.errorTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.errorMismatch"));
      return;
    }
    inFlight.current = true;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("reset.errorGeneric"));
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f9f9f8]" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-800 to-green-900 p-12 text-white items-center">
        <div className="max-w-md">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-xl">Tasklio AI</span>
          </Link>
          <h2 className="font-display mt-16 text-4xl font-semibold tracking-tight leading-tight">
            {t("reset.sidebarTitle")}
          </h2>
          <p className="mt-5 text-green-50 leading-relaxed">
            {t("reset.sidebarBody")}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden inline-flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="font-display font-bold text-lg">Tasklio AI</span>
            </Link>
            <div className="ml-auto">
              <LanguageSwitcher testidPrefix="reset-lang-switch" />
            </div>
          </div>

          {done ? (
            <div data-testid="reset-success-state">
              <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="font-display mt-5 text-3xl font-semibold text-gray-900" data-testid="reset-success-title">
                {t("reset.successTitle")}
              </h1>
              <p className="mt-3 text-gray-600 leading-relaxed" data-testid="reset-success-message">
                {t("reset.successBody")}
              </p>
              <Link
                to="/login"
                className="mt-8 w-full h-11 inline-flex items-center justify-center rounded-md bg-green-800 hover:bg-green-900 text-white font-medium transition-colors"
                data-testid="reset-success-login-link"
              >
                {t("reset.goToLogin")}
              </Link>
            </div>
          ) : tokenMissing ? (
            <div data-testid="reset-missing-token-state">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h1 className="font-display mt-5 text-3xl font-semibold text-gray-900">
                {t("reset.missingTitle")}
              </h1>
              <p className="mt-3 text-gray-600 leading-relaxed">
                {t("reset.missingBody")}
              </p>
              <Link
                to="/forgot-password"
                className="mt-8 w-full h-11 inline-flex items-center justify-center rounded-md bg-green-800 hover:bg-green-900 text-white font-medium transition-colors"
                data-testid="reset-request-new-link"
              >
                {t("reset.requestNew")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-gray-900" data-testid="reset-password-title">
                {t("reset.title")}
              </h1>
              <p className="mt-2 text-gray-500">
                {t("reset.subtitle")}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="password">{t("reset.newPassword")}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.passwordPlaceholder")}
                    required
                    minLength={6}
                    className="mt-2 h-11"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm">{t("reset.confirmPassword")}</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t("reset.confirmPlaceholder")}
                    required
                    minLength={6}
                    className="mt-2 h-11"
                    data-testid="reset-confirm-input"
                  />
                </div>

                {error ? (
                  <p
                    className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                    data-testid="reset-error-message"
                  >
                    {error}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-green-800 hover:bg-green-900 text-white"
                  data-testid="reset-submit-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reset.submit")}
                </Button>
              </form>

              <p className="mt-8 text-sm text-gray-500 text-center">
                <Link to="/login" className="text-green-800 hover:underline font-medium" data-testid="reset-back-to-login">
                  {t("forgot.backToLogin")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
